import { makeAutoObservable } from "mobx"
import OrganizationAPI from "../utilities/OrganizationsHelper"
import endpoints from '../../endpoints'

export const DEFAULT_ORG = "DEFAULT"

export default class Organizations {
  
  title = 'Organizations'
  list = []
  selected = DEFAULT_ORG
  initialFetch = false
  orgMap = new Map();
  selectedOrgId = DEFAULT_ORG
  pagination = { total: 0, limit: 0, offset: 0, hasMore: false }
  currentSearch = ''
  // Monotonic id stamped on every search/load-more request. Only the most
  // recently issued request may publish its results, so a slow earlier response
  // (debounced keystroke, dropdown reopen, in-flight load-more) can't clobber a
  // newer query's list. See HMB-18829 review.
  latestRequestId = 0

  constructor(){
    makeAutoObservable(this)
  }

  // Accumulate name -> id mappings so a previously-selected org stays resolvable
  // even after the displayed list changes on a later search/page.
  mergeOrgMap = (orgs = []) => {
    for (const org of orgs) {
      this.orgMap.set(org?.name, org?._id || org?.id)
    }
    if (this.selected) {
      this.selectedOrgId = this.orgMap.get(this.selected)
    }
  }

  /**
   * Fetch the first page of organizations matching `query` from the server
   * (server-side search), replacing the dropdown's displayed list.
   * @param {string} [query]
   */
  fetchOrgsBySearch = async (query = '') => {
    const requestId = ++this.latestRequestId
    const { data, pagination } = await OrganizationAPI.fetchOrganizationsBySearch(
      endpoints.WEB_APP, query, { offset: 0 }
    )
    // Record name->id mappings even from a superseded response: they're stable
    // facts, and callers that resolve a specific org by name off this promise
    // (e.g. Permissions selecting "Hi Marley") must see its id regardless of
    // which concurrent search won the list.
    this.mergeOrgMap(data)
    // A newer search/load started while this was in flight — keep its list and
    // don't overwrite with this stale page.
    if (requestId !== this.latestRequestId) return
    this.currentSearch = query
    this.list = data
    this.pagination = pagination
    this.initialFetch = true
  }

  /**
   * Append the next page of results for the current search, if any remain.
   * Backs a "load more" / infinite-scroll affordance in the dropdown.
   */
  loadMoreOrgs = async () => {
    if (!this.pagination?.hasMore) return
    const requestId = ++this.latestRequestId
    const searchAtRequest = this.currentSearch
    const nextOffset = this.pagination.offset + this.pagination.limit
    const { data, pagination } = await OrganizationAPI.fetchOrganizationsBySearch(
      endpoints.WEB_APP, searchAtRequest, { offset: nextOffset }
    )
    // Stable name->id facts are safe to record regardless of staleness.
    this.mergeOrgMap(data)
    // Drop the page if a newer request superseded this one or the active search
    // changed underneath us, so page-N rows of an old query aren't appended to a
    // different result set.
    if (requestId !== this.latestRequestId || searchAtRequest !== this.currentSearch) return
    this.list = [...this.list, ...data]
    this.pagination = pagination
  }

  setSelected = selected => {
    this.selectedOrgId = this.orgMap.get(selected)
    this.selected = selected
  }

  /**
   * Derive the sorted display names for the dropdown. Pure read of `list` — the
   * name→id map is maintained by {@link mergeOrgMap} at fetch time, so this is
   * safe to call inline during an observer's render without mutating observables.
   * @param {string} [route] - when 'templates', prepends the DEFAULT_ORG option
   */
  orgNames(route){
    const names = this.list ? this.list.map(org => org.name) : []
    names.sort()
    if (route === 'templates') names.unshift(DEFAULT_ORG)
    return names
  }

  /**
   * Creates an organization with advanced configuration
   * @param {Object} orgData - Organization data object
   * @param {string} orgData.organizationName - Name of the organization
   * @param {string} orgData.identityProvider - Authentication type (MARLEY or FEDERATED)
   * @param {string} [orgData.domainName] - Domain name for FEDERATED auth
   * @param {boolean} [orgData.createSupportUser=false] - Whether to create a support user for the organization
   * @param {Object} [orgData.integration] - Integration configuration
   * @param {string} [orgData.integration.authentication] - Authentication method (KEY or OAUTH2)
   * @param {Object} [user] - User information for logging
   * @returns {Promise<Object>} The created organization object
   */
  createOrganization = async (orgData) => {
    try {
      const newOrg = await OrganizationAPI.createOrganizationRequest(orgData, endpoints.WEB_APP)
      
      // Refresh the first page of the org list so it stays consistent with the
      // paginated dropdown state. A separate op that shouldn't fail the creation.
      try {
        await this.fetchOrgsBySearch('')
      } catch (refreshError) {
        console.warn('Failed to refresh organizations list after creation:', refreshError)
      }
      
      return newOrg
    } catch (error) {
      console.error('Error creating organization:', error)
      throw error
    }
  }
}